import { useCMSPageField } from '@/cms/fields'
import { groupBookingsAPIClient } from '@/infrastructure/groups/api'
import { Custom500 } from '@/pages/500'
import { CheckoutError } from '@/scopes/checkout/components/CheckoutError'
import { CheckoutHeading, CheckoutHeadingTitle } from '@/scopes/checkout/components/CheckoutHeading'
import { CheckoutNextStep } from '@/scopes/checkout/components/CheckoutNextStep'
import { CheckoutProgress } from '@/scopes/checkout/components/CheckoutProgress'
import { CheckoutContextProvider } from '@/scopes/checkout/domain/CheckoutContext'
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks'
import { CheckoutSummary } from '@/scopes/checkout/groups/components/CheckoutSummary'
import {
  CheckoutTermsAndConditions,
  Loader,
  MainContent,
  PageLayout,
  Wrapper,
} from '@/scopes/checkout/groups/components/Layout'
import { PreviousStep } from '@/scopes/checkout/groups/components/PreviousStep'
import { GroupsDependenciesProvider } from '@/scopes/checkout/groups/dependencies/context'
import { checkoutAPIClient } from '@/scopes/checkout/infrastructure/api'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import createNextRouter from '@/scopes/common/infrastructure/router/nextRouter'
import { SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { Header } from '../../groups/components/Header'

const queryClient = new QueryClient()
const Content = styled.div`
  display: flex;
  position: relative;
  gap: ${tk('spacing_md')};
  justify-content: center;
  max-width: ${tk('breakpoint_desktop_xl')};
  flex-direction: column;

  @media ${tk('mq_desktop_md')} {
    flex-direction: row;
    gap: ${tk('spacing_lg')};
  }
`
function Page() {
  const { isLoading, data } = useBookingDraftQuery()
  const intl = useIntl()

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      {data?.error && data.error.hasError && <CheckoutError showRedirectButton />}
      {(!data?.error || (data?.error && !data.error.hasError)) && (
        <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
          <CheckoutProgress />
          <Content>
            <MainContent>
              <CheckoutHeading direction="y">
                <CheckoutHeadingTitle>
                  {intl.formatMessage({ id: 'groups_checkout_steps_header_overview_step_title' })}
                </CheckoutHeadingTitle>
              </CheckoutHeading>
              <CheckoutSummary />
              <CheckoutTermsAndConditions>
                <Typography
                  $variant="body-md"
                  dangerouslySetInnerHTML={{
                    __html: intl.formatMessage({ id: 'groups_checkout_steps_overview_step_conditions' }),
                  }}
                />
              </CheckoutTermsAndConditions>
              <CheckoutNextStep />
            </MainContent>
          </Content>
        </SpaceBetween>
      )}
    </>
  )
}

export default function CheckoutPageOverview() {
  const contextItemId = useCMSPageField('contextItemId')
  const router = useRouter()

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutContextProvider
        value={{
          contextItemId,
        }}
      >
        <GroupsDependenciesProvider
          dependencies={{ checkoutAPIClient, groupBookingsAPIClient, router: createNextRouter(router) }}
        >
          <PageLayout>
            <Header hideLiveSearch>
              <PreviousStep />
            </Header>
            <NewRelicErrorBoundary scope="groups-page" fallback={<Custom500 brand="sunweb" />}>
              <Wrapper>
                <Page />
              </Wrapper>
            </NewRelicErrorBoundary>
          </PageLayout>
        </GroupsDependenciesProvider>
        <DevTools />
      </CheckoutContextProvider>
    </QueryClientProvider>
  )
}
